import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Map, View } from 'ol/index.js';
import { fromLonLat } from 'ol/proj.js';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer.js';
import { OSM, Vector as VectorSource } from 'ol/source.js';
import { Feature } from 'ol/index.js';
import { Point } from 'ol/geom.js';
import { Style, Circle, Fill, Stroke } from 'ol/style.js';
import { Overlay } from 'ol/index.js';
import GridTableTopBar from '../../../components/GridTable/GridTableTopBar';
import CustomColoredCheckbox from '../../../components/checkbox/CustomColoredCheckbox';
import UserSummary from './UserSummary';
import 'ol/ol.css';
import useApi from '../../../services/useApi';
import { DynamicFilterConfig } from '../../../components/DynamicComponent/DynamicFilterPanel';
import dayjs from 'dayjs';

interface UserCounts {
  ADM?: number;
  MDST?: number;
  DST?: number;
  SDST?: number;
  AGT?: number;
  RET?: number;
  CMR?: number;
}

interface LocationData {
  latitude: number | null;
  longitude: number | null;
  country: string;
  province: string | null;
  region: string | null;
  users: UserCounts;
}

interface User {
  id: string;
  lat: number;
  lng: number;
  type: 'admin' | 'master_distributor' | 'distributor' | 'sub_distributor' | 'retailer' | 'customer' | 'agents';
  country: string;
  province: string;
  name: string;
}


const USER_COLORS = {
  admin: '#FFC107',
  master_distributor: '#E91E63',
  distributor: '#FF9800',
  sub_distributor: '#000000',
  agents: '#D925B2',
  retailer: '#2196F3',
  customer: '#4CAF50'
};

const USER_LABELS = {
  admin: 'Admins',
  master_distributor: 'Master Distributors',
  distributor: 'Distributors',
  sub_distributor: 'Sub Distributors',
  agents: 'Agents',
  retailer: 'Retailers',
  customer: 'Customers'
};

// Country coordinates for fallback positioning
const COUNTRY_COORDINATES: Record<string, [number, number]> = {
  'United States': [-95.7129, 37.0902],
  'Canada': [-106.3468, 56.1304],
  'United Kingdom': [-3.4360, 55.3781],
  'Germany': [10.4515, 51.1657],
  'France': [2.2137, 46.2276],
  'Spain': [-3.7492, 40.4637],
  'Italy': [12.5674, 41.8719],
  'India': [78.9629, 20.5937],
  'China': [104.1954, 35.8617],
  'Japan': [138.2529, 36.2048],
  'Australia': [133.7751, -25.2744],
  'Brazil': [-51.9253, -14.2350],
  'Mexico': [-102.5528, 23.6345],
  'Russia': [105.3188, 61.5240],
  'South Africa': [22.9375, -30.5595],
  'Nigeria': [8.6753, 9.0820],
  'Egypt': [30.8025, 26.8206],
  'Turkey': [35.2433, 38.9637],
  'Iran': [53.6880, 32.4279],
  'Saudi Arabia': [45.0792, 23.8859],
  'UAE': [53.8478, 23.4241],
  'Pakistan': [69.3451, 30.3753],
  'Bangladesh': [90.3563, 23.6850],
  'Indonesia': [113.9213, -0.7893],
  'Thailand': [100.9925, 15.8700],
  'Vietnam': [108.2772, 14.0583],
  'Philippines': [121.7740, 12.8797],
  'Malaysia': [101.9758, 4.2105],
  'Singapore': [103.8198, 1.3521],
  'South Korea': [127.7669, 35.9078],
  'North Korea': [127.5101, 40.3399],
  'Mongolia': [103.8467, 46.8625],
  'Kazakhstan': [66.9237, 48.0196],
  'Uzbekistan': [64.5853, 41.3775],
  'Afghanistan': [67.7101, 33.9391],
  'Iraq': [43.6793, 33.2232],
  'Syria': [38.9968, 34.8021],
  'Lebanon': [35.8623, 33.8547],
  'Jordan': [36.2384, 30.5852],
  'Israel': [34.8516, 31.0461],
  'Palestine': [35.2332, 31.9522],
  'Kuwait': [47.4818, 29.3117],
  'Qatar': [51.1839, 25.3548],
  'Bahrain': [50.6378, 25.9304],
  'Oman': [55.9233, 21.4735],
  'Yemen': [48.5164, 15.5527],
  'Ethiopia': [40.4897, 9.1450],
  'Kenya': [37.9062, -0.0236],
  'Tanzania': [34.8888, -6.3690],
  'Uganda': [32.2903, 1.3733],
  'Ghana': [-1.0232, 7.9465],
  'Morocco': [-7.0926, 31.6295],
  'Algeria': [1.6596, 28.0339],
  'Tunisia': [9.5375, 33.8869],
  'Libya': [17.2283, 26.3351],
  'Sudan': [30.2176, 12.8628],
  'Chad': [18.7322, 15.4542],
  'Mali': [-3.9962, 17.5707],
  'Senegal': [-14.4524, 14.4974],
  'Guinea': [-9.6966, 9.6412],
  'Sierra Leone': [-11.7799, 8.4606],
  'Liberia': [-9.4295, 6.4281],
  'Ivory Coast': [-5.5471, 7.5400],
  'Burkina Faso': [-2.1976, 12.2383],
  'Cameroon': [12.3547, 7.3697],
  'Central African Republic': [20.9394, 6.6111],
  'Democratic Republic of Congo': [21.7587, -4.0383],
  'Republic of Congo': [21.7587, -0.2280],
  'Gabon': [11.6094, -0.8037],
  'Equatorial Guinea': [10.2679, 1.6508],
  'Angola': [17.8739, -11.2027],
  'Zambia': [27.8493, -13.1339],
  'Zimbabwe': [29.1549, -19.0154],
  'Botswana': [24.6849, -22.3285],
  'Namibia': [18.4904, -22.9576],
  'Lesotho': [28.2336, -29.6100],
  'Swaziland': [31.4659, -26.5225],
  'Madagascar': [46.8691, -18.7669],
  'Mauritius': [57.5522, -20.3484],
  'Seychelles': [55.4919, -4.6796],
  'Comoros': [43.8722, -11.8750],
  'Djibouti': [42.5903, 11.8251],
  'Somalia': [46.1996, 5.1521],
  'Eritrea': [39.7823, 15.1794],
  'Rwanda': [29.8740, -1.9403],
  'Burundi': [29.9189, -3.3731],
  'Malawi': [34.3015, -13.2543],
  'Mozambique': [35.5296, -18.6657],
  'Argentina': [-63.6167, -38.4161],
  'Chile': [-71.5430, -35.6751],
  'Peru': [-75.0152, -9.1900],
  'Colombia': [-74.2973, 4.5709],
  'Venezuela': [-66.5897, 6.4238],
  'Ecuador': [-78.1834, -1.8312],
  'Bolivia': [-63.5887, -16.2902],
  'Paraguay': [-58.4438, -23.4425],
  'Uruguay': [-55.7658, -32.5228],
  'Guyana': [-58.9302, 4.8604],
  'Suriname': [-56.0278, 3.9193],
  'French Guiana': [-53.1258, 3.9339],
  'Guatemala': [-90.2308, 15.7835],
  'Belize': [-88.4976, 17.1899],
  'El Salvador': [-88.8965, 13.7942],
  'Honduras': [-87.1734, 15.2000],
  'Nicaragua': [-85.2072, 12.2658],
  'Costa Rica': [-83.7534, 9.7489],
  'Panama': [-80.7821, 8.5380],
  'Cuba': [-77.7812, 21.5218],
  'Jamaica': [-77.2975, 18.1096],
  'Haiti': [-72.2852, 18.9712],
  'Dominican Republic': [-70.1627, 18.7357],
  'Puerto Rico': [-66.5901, 18.2208],
  'Trinidad and Tobago': [-61.2225, 10.6918],
  'Barbados': [-59.5432, 13.1939],
  'Grenada': [-61.6040, 12.2626],
  'Saint Vincent and the Grenadines': [-61.2872, 12.9843],
  'Saint Lucia': [-60.9789, 13.9094],
  'Dominica': [-61.3709, 15.4150],
  'Antigua and Barbuda': [-61.7964, 17.0608],
  'Saint Kitts and Nevis': [-62.7829, 17.3578],
  'Norway': [8.4689, 60.4720],
  'Sweden': [18.6435, 60.1282],
  'Finland': [25.7482, 61.9241],
  'Denmark': [9.5018, 56.2639],
  'Iceland': [-19.0208, 64.9631],
  'Ireland': [-8.2439, 53.4129],
  'Netherlands': [5.2913, 52.1326],
  'Belgium': [4.4699, 50.5039],
  'Luxembourg': [6.1296, 49.8153],
  'Switzerland': [8.2275, 46.8182],
  'Austria': [14.5501, 47.5162],
  'Czech Republic': [15.4730, 49.8175],
  'Slovakia': [19.6990, 48.6690],
  'Poland': [19.1342, 51.9194],
  'Hungary': [19.5033, 47.1625],
  'Slovenia': [14.9955, 46.1512],
  'Croatia': [15.2000, 45.1000],
  'Bosnia and Herzegovina': [17.6791, 43.9159],
  'Serbia': [21.0059, 44.0165],
  'Montenegro': [19.3744, 42.7087],
  'Albania': [20.1683, 41.1533],
  'North Macedonia': [21.7453, 41.6086],
  'Bulgaria': [25.4858, 42.7339],
  'Romania': [24.9668, 45.9432],
  'Moldova': [28.3699, 47.4116],
  'Ukraine': [31.1656, 48.3794],
  'Belarus': [27.9534, 53.7098],
  'Lithuania': [23.8813, 55.1694],
  'Latvia': [24.6032, 56.8796],
  'Estonia': [25.0136, 58.5953],
  'Portugal': [-8.2245, 39.3999],
  'Greece': [21.8243, 39.0742],
  'Cyprus': [33.4299, 35.1264],
  'Malta': [14.3754, 35.9375],
  'Monaco': [7.4128, 43.7384],
  'San Marino': [12.4578, 43.9424],
  'Vatican City': [12.4534, 41.9029],
  'Liechtenstein': [9.5554, 47.1660],
  'Andorra': [1.6016, 42.5462]
};

export default function UserMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const { getAPI } = useApi();
  const mapInstanceRef = useRef<Map | null>(null);
  const overlayRef = useRef<Overlay | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserTypes, setSelectedUserTypes] = useState<Set<string>>(
    new Set(['admin', 'master_distributor', 'distributor', 'sub_distributor', 'retailer', 'customer', 'agents'])
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSearchParams, setInternalSearchParams] = useState<URLSearchParams>(new URLSearchParams());
  const [initialUsersCount, setInitialUsersCount] = useState<number>(0);
  const fetchingRef = useRef(false);
  const lastFetchParamsRef = useRef<string>('');
  const isTooltipOpenRef = useRef(false);

  // Extract search params values to avoid infinite re-renders
  // Note: We now use local state instead of searchParams for filters

  // Transform API response to user structure
  const transformLocationDataToUsers = useCallback((locationData: LocationData[]): User[] => {
    const users: User[] = [];
    let userId = 1;
    locationData.forEach((location) => {
      // Get geographic coordinates directly from the API response
      let lat: number, lng: number;

      if (location.latitude !== null && location.longitude !== null &&
        location.latitude !== 0 && location.longitude !== 0) {
        // Use exact coordinates from API
        lat = location.latitude;
        lng = location.longitude;
      } else {
        // Fallback to country coordinates
        const countryCoords = COUNTRY_COORDINATES[location.country];
        if (countryCoords) {
          [lng, lat] = countryCoords;
        } else {
          // Default to 0,0 if no coordinates available
          lat = 0;
          lng = 0;
        }
      }

      // Transform each user type count to individual users
      Object.entries(location.users).forEach(([userType, count]) => {
        if (count && count > 0) {
          // Map API user types to old user types
          const typeMapping: Record<string, User['type']> = {
            'ADM': 'admin',
            'MDST': 'master_distributor',
            'DST': 'distributor',
            'SDST': 'sub_distributor',
            'AGT': 'agents',
            'RET': 'retailer',
            'CMR': 'customer'
          };

          const userTypeOld = typeMapping[userType] || 'customer';

          // Create individual users for each count
          for (let i = 0; i < count; i++) {
            users.push({
              id: `${userId++}`,
              lat: lat,
              lng: lng,
              type: userTypeOld,
              country: location.country || 'Unknown',
              province: location.province || 'Unknown',
              name: `${userTypeOld.charAt(0).toUpperCase() + userTypeOld.slice(1)} ${userId}`
            });
          }
        }
      });
    });

    return users;
  }, []);

  const fetchUsers = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current) {
      return;
    }

    // Close tooltip if it's open when fetchUsers is called
    if (isTooltipOpenRef.current) {
      const overlay = overlayRef.current;
      if (overlay && overlay.getPosition()) {
        overlay.setPosition(undefined);
        isTooltipOpenRef.current = false;
      }
    }

    // Build query parameters
    const params = new URLSearchParams();

    // Add user_type parameter only if some (but not all) user types are selected
    const allUserTypes = ['admin', 'master_distributor', 'distributor', 'sub_distributor', 'retailer', 'customer', 'agents'];
    const allSelected = allUserTypes.every(type => selectedUserTypes.has(type));
    const noneSelected = selectedUserTypes.size === 0;

    if (!allSelected && !noneSelected) {
      // Map old user types to API user types
      const typeMapping: Record<string, string> = {
        'admin': 'ADM',
        'master_distributor': 'MDST',
        'distributor': 'DST',
        'sub_distributor': 'SDST',
        'agents': 'AGT',
        'retailer': 'RET',
        'customer': 'CMR'
      };

      const apiUserTypes = Array.from(selectedUserTypes).map(type => typeMapping[type] || type);
      params.append('user_type', apiUserTypes.join(','));
    }

    // Add search parameter
    if (searchTerm) {
      params.append('search', searchTerm);
    }

    // Add user_activity parameter
    const userActivity = internalSearchParams.get('user_activity');
    if (userActivity) {
      params.append('user_activity', userActivity);
    }

    // Add date range parameters
    const startDate = internalSearchParams.get('start_date');
    const endDate = internalSearchParams.get('end_date');
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `dashboard/users/map?${queryString}` : 'dashboard/users/map';

    // Check if parameters have actually changed
    const currentParams = `${selectedUserTypes.size}-${Array.from(selectedUserTypes).sort().join(',')}-${searchTerm}-${internalSearchParams.toString()}`;
    if (lastFetchParamsRef.current === currentParams) {
      return;
    }

    lastFetchParamsRef.current = currentParams;

    try {
      fetchingRef.current = true;
      setLoading(true);
      const response = await getAPI(url);
      if (response && response.data && Array.isArray(response.data.data)) {
        // Transform the location data to user structure
        const transformedUsers = transformLocationDataToUsers(response.data.data);
        setUsers(transformedUsers);
        
        // Set initial users count only on first successful API call
        if (initialUsersCount === 0 && transformedUsers.length > 0) {
          setInitialUsersCount(transformedUsers.length);
        }
      } else {
        console.warn('Invalid API response structure, setting empty array');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [selectedUserTypes, searchTerm, internalSearchParams, getAPI, transformLocationDataToUsers, initialUsersCount]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) return;

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM({
            // Use CartoDB Positron tiles which have English labels
            url: 'https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
            attributions: '',
            maxZoom: 20
          })
        })
      ],
      view: new View({
        center: fromLonLat([0, 0]),
        zoom: 2,
        minZoom: 1,
        maxZoom: 18
      })
    });

    mapInstanceRef.current = map;

    // Create overlay for popups
    const overlay = new Overlay({
      element: document.createElement('div'),
      positioning: 'bottom-center',
      stopEvent: false,
      offset: [0, -15],
      autoPan: {
        animation: {
          duration: 250
        }
      }
    });
    map.addOverlay(overlay);
    overlayRef.current = overlay;

    // Add cursor change functionality for map interactions
    const mapElement = mapRef.current;
    let isDragging = false;
    let isZooming = false;
    let zoomTimeout: NodeJS.Timeout | null = null;
    
    // Track dragging state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('pointerdown' as any, () => {
      isDragging = false;
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('pointerdrag' as any, () => {
      isDragging = true;
      mapElement.style.cursor = 'grabbing';
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('pointerup' as any, () => {
      if (isDragging) {
        mapElement.style.cursor = 'default';
      }
      isDragging = false;
    });

    // Handle double-click and drag
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('dblclick' as any, () => {
      mapElement.style.cursor = 'grabbing';
    });

    // Reset cursor when mouse leaves map
    mapElement.addEventListener('mouseleave', () => {
      mapElement.style.cursor = 'default';
      isDragging = false;
      isZooming = false;
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
        zoomTimeout = null;
      }
    });

    // Handle zoom with continuous cursor display
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('wheel' as any, (event: any) => {
      // Clear any existing timeout
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
      }

      if (event.originalEvent.deltaY < 0) {
        mapElement.style.cursor = 'zoom-in';
        isZooming = true;
      } else {
        mapElement.style.cursor = 'zoom-out';
        isZooming = true;
      }

      // Set timeout to reset cursor after zoom stops
      zoomTimeout = setTimeout(() => {
        if (isZooming) {
          mapElement.style.cursor = 'default';
          isZooming = false;
        }
      }, 300);
    });

    return () => {
      map.setTarget(undefined);
    };
  }, []);


  // Create location style based on user types
  const createLocationStyle = (userType: string, isCluster: boolean = false) => {
    const color = USER_COLORS[userType as keyof typeof USER_COLORS];
    const radius = isCluster ? 12 : 8;

    return new Style({
      image: new Circle({
        radius,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#ffffff', width: 2 })
      })
    });
  };

  // Update map with users data
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;

    // Remove existing vector layers
    const existingLayers = map.getLayers().getArray().filter((layer: unknown) =>
      layer instanceof VectorLayer
    );
    existingLayers.forEach((layer) => map.removeLayer(layer as VectorLayer));

    // Filter users based on selected user types
    const filteredUsers = Array.isArray(users) ? users.filter(user =>
      selectedUserTypes.has(user.type)
    ) : [];

    // Get current zoom level
    const currentZoom = map.getView().getZoom() || 0;

    // Group users by coordinates for clustering based on zoom level
    const groupedUsers = filteredUsers.reduce((acc, user) => {
      const coords = fromLonLat([user.lng, user.lat]) as [number, number];

      // Use different precision based on zoom level
      // Higher zoom = more precision (smaller clusters)
      const precision = currentZoom > 10 ? 1000 : currentZoom > 6 ? 100 : 10;
      const key = `${Math.round(coords[0] * precision) / precision}-${Math.round(coords[1] * precision) / precision}`;

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push({ ...user, coords });

      return acc;
    }, {} as Record<string, Array<User & { coords: [number, number] }>>);

    const features: Feature[] = [];

    Object.entries(groupedUsers).forEach(([, userGroup]) => {
      const coords = userGroup[0].coords;

      if (userGroup.length === 1) {
        // Single user
        const user = userGroup[0];
        const feature = new Feature({
          geometry: new Point(coords as [number, number]),
          user: user,
          type: 'single'
        });

        feature.setStyle(createLocationStyle(user.type));
        features.push(feature);
      } else {
        // Multiple users - create a cluster marker
        const feature = new Feature({
          geometry: new Point(coords as [number, number]),
          users: userGroup,
          type: 'cluster',
          count: userGroup.length
        });

        // Count users by type for styling
        const userCounts = userGroup.reduce((acc, user) => {
          acc[user.type] = (acc[user.type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        // Use the dominant user type for styling
        const dominantType = Object.entries(userCounts).reduce((a, b) =>
          (userCounts[a[0]] || 0) > (userCounts[b[0]] || 0) ? a : b
        )[0];

        feature.setStyle(createLocationStyle(dominantType, true));
        features.push(feature);
      }
    });

    // Create vector layer
    const vectorSource = new VectorSource({ features });
    const vectorLayer = new VectorLayer({
      source: vectorSource
    });

    map.addLayer(vectorLayer);

    // Add click handlers
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('click' as any, (event: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const feature = map.forEachFeatureAtPixel(event.pixel, (feature: any) => feature);

      if (feature) {
        const overlay = overlayRef.current;
        if (overlay) {
          const element = overlay.getElement();
          if (element) {
            if (feature.get('type') === 'single') {
              const user = feature.get('user');

              element.innerHTML = `
                <div class="bg-white p-4 rounded-lg border max-w-sm">
                  <div class="mb-3">
                    <div class="text-lg font-bold text-gray-800 mb-2">Total Users: 1</div>
                    <div class="space-y-1">
                      <div class="flex items-center justify-between text-sm">
                        <div class="flex items-center space-x-2">
                          <div class="w-3 h-3 rounded-sm" style="background-color: ${USER_COLORS[user.type as keyof typeof USER_COLORS]}"></div>
                          <span>${USER_LABELS[user.type as keyof typeof USER_LABELS]}:</span>
                        </div>
                        <span class="font-medium pl-1">1</span>
                      </div>
                    </div>
                  </div>
                </div>
              `;
            } else {
              const users = feature.get('users');
              const userCounts = users.reduce((acc: Record<string, number>, user: User) => {
                acc[user.type] = (acc[user.type] || 0) + 1;
                return acc;
              }, {} as Record<string, number>);

              const totalUsers = users.length;

              element.innerHTML = `
                <div class="bg-white p-4 rounded-lg  border max-w-sm">
                  <div class="mb-3">
                    <div class="text-lg font-bold text-gray-800 mb-2">Total Users: ${totalUsers}</div>
                    <div class="space-y-1">
                      ${Object.entries(userCounts).map(([userType, count]) => `
                        <div class="flex items-center justify-between text-sm">
                          <div class="flex items-center space-x-2">
                            <div class="w-3 h-3 rounded-sm" style="background-color: ${USER_COLORS[userType as keyof typeof USER_COLORS]}"></div>
                            <span>${USER_LABELS[userType as keyof typeof USER_LABELS]}:</span>
                          </div>
                          <span class="font-medium pl-2">${count}</span>
                        </div>
                      `).join('')}
                    </div>
                  </div>
                </div>
              `;
            }
             overlay.setPosition(event.coordinate);
             isTooltipOpenRef.current = true;
          }
        }
      } else {
        // Hide popup when clicking on empty area
        const overlay = overlayRef.current;
        if (overlay) {
          overlay.setPosition(undefined);
          isTooltipOpenRef.current = false;
        }
      }
    });

    // Auto-fit to users or zoom out if no data
    if (features.length > 0) {
      const extent = vectorSource.getExtent();
      map.getView().fit(extent, { padding: [50, 50, 50, 50] });
    } else {
      // Zoom out completely when no data
      map.getView().animate({
        center: fromLonLat([0, 0]),
        zoom: 1,
        duration: 500
      });
    }

  }, [users, selectedUserTypes]);

  // Dynamic filter config for transactions
  const dynamicFilterConfig: DynamicFilterConfig = useMemo(() => ({
    "Date Range": {
      label: "Date Range",
      type: "date",
      required: false,
      searchKey: {
        start: "start_date",
        end: "end_date",
      },
    },
    "User Activity": {
      label: "User Activity",
      type: "checkbox",
      options: ["Active", "Inactive", "Dormant"],
      required: false,
      searchKey: "user_activity",
    },
  }), []);

  const handleFilter = useCallback(
    (filters: { [key: string]: string | boolean | null } | null) => {
      const newParams = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
              newParams.set(key, value.join(','));
            } else if (key === 'start_date' || key === 'end_date') {
              // Format dates to YYYY-MM-DD format
              const dateValue = dayjs(value as string).format('YYYY-MM-DD');
              newParams.set(key, dateValue);
            } else {
              newParams.set(key, String(value));
            }
          }
        });
      }
      
      setInternalSearchParams(newParams);
    },
    []
  );

  const handleSearch = useCallback((searchTerm: string) => {
    setSearchTerm(searchTerm);
  }, []);

  // Handle user type filter
  const handleUserTypeToggle = (type: string) => {
    const newSelected = new Set(selectedUserTypes);
    if (newSelected.has(type)) {
      newSelected.delete(type);
    } else {
      newSelected.add(type);
    }
    setSelectedUserTypes(newSelected);
    // Don't update URL params, just update local state
  };


  // Get user counts by type from users data
  const getUserCounts = () => {
    if (!Array.isArray(users)) {
      return {
        admin: 0,
        master_distributor: 0,
        distributor: 0,
        sub_distributor: 0,
        agents: 0,
        retailer: 0,
        customer: 0
      };
    }

    return {
      admin: users.filter(user => user.type === 'admin').length,
      master_distributor: users.filter(user => user.type === 'master_distributor').length,
      distributor: users.filter(user => user.type === 'distributor').length,
      sub_distributor: users.filter(user => user.type === 'sub_distributor').length,
      agents: users.filter(user => user.type === 'agents').length,
      retailer: users.filter(user => user.type === 'retailer').length,
      customer: users.filter(user => user.type === 'customer').length,
    };
  };

  const userCounts = getUserCounts();

  return (
    <div className="w-full bg-white rounded-lg">
      <div className="px-4 pt-4 pb-2 text-2xl font-semibold text-primary_text">Users On The Platform: <span className="text-black">{initialUsersCount || users.length}</span></div>
      {/* GridTableTopBar */}
      <GridTableTopBar
        onSearch={handleSearch}
        onFilter={handleFilter}
        searchValue={searchTerm}
        loading={loading}
        searchPlaceholder="Search by country and province"
        searchParams={internalSearchParams} // Use internal search params for filter prefilling
        disableSearchAndFilter={false}
        filters={true}
        exporting={false} 
        disableExport={true} 
        dynamicFilterConfig={dynamicFilterConfig}  
        removeBorder={true} customHeight="h-28" />
      {/* User Type Checkboxes */}
      <div className="bg-white rounded-lg p-2 mb-4">
        <div className="flex flex-wrap gap-4">
          {Object.entries(USER_COLORS).map(([type, color]) => (
            <CustomColoredCheckbox
              key={type}
              id={`user-type-${type}`}
              checked={selectedUserTypes.has(type)}
              onChange={() => handleUserTypeToggle(type)}
              color={color}
              label={USER_LABELS[type as keyof typeof USER_LABELS]}
              count={userCounts[type as keyof typeof userCounts]}
            />
          ))}
        </div>
      </div>


      {/* Map */}
      <div className=" rounded-lg mx-4 mt-4 -mb-2 p-4 border border-gray-100 relative">
        <div ref={mapRef} className="w-full border h-96" />
        {loading && (
          <div className="absolute inset-0 bg-black/50 flex items-center rounded-lg justify-center z-10">
            <div className="flex flex-col items-center space-y-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              <p className="text-sm text-white font-medium">Loading</p>
            </div>
          </div>
        )}
      </div>

      {/* User Summary */}
      <UserSummary
        userCounts={userCounts}
        userColors={USER_COLORS}
        userLabels={USER_LABELS}
      />
      <hr className="border-gray-100 mt-4 mx-4" />
    </div>
  );
}